import { useEffect, useState } from "react";
import { X, Loader2, Users, PenLine, KeyRound, HardDrive, ReplyAll, MailPlus, Check, Building2 } from "lucide-react";
import { toast } from "sonner";
import { SenderAvatar } from "./MailApp";

const SECTIONS = [
  { id: "accounts", label: "Accounts", Icon: Users },
  { id: "signature", label: "Signature", Icon: PenLine },
  { id: "password", label: "Password", Icon: KeyRound },
  { id: "storage", label: "Storage", Icon: HardDrive },
  { id: "autoreply", label: "Automatic replies", Icon: ReplyAll },
];

const fmtMB = (kb) => (kb >= 1048576 ? `${(kb / 1048576).toFixed(1)} GB` : `${(kb / 1024).toFixed(0)} MB`);

export const SettingsModal = ({ api, accounts, activeEmail, canAddAccount = true, onClose, onAddAccount, onRemoveAccount, onOpenSignature }) => {
  const [section, setSection] = useState("accounts");
  const [isCeo, setIsCeo] = useState(false);
  useEffect(() => {
    api.get("/portal/company/can").then(({ data }) => setIsCeo(data.ceo)).catch(() => {});
  }, [api]);
  const sections = isCeo ? [...SECTIONS, { id: "company", label: "Company", Icon: Building2 }] : SECTIONS;

  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6" data-testid="settings-modal" onClick={onClose}>
      <div className="w-full max-w-3xl bg-nx-navy2 border border-white/10 shadow-2xl h-[85vh] sm:h-[560px] flex overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* left nav — Outlook settings style */}
        <nav className="w-40 sm:w-48 shrink-0 border-r border-white/10 bg-white/[0.02] py-3" data-testid="settings-nav">
          <p className="px-4 pb-2 text-[10px] tracking-[0.2em] uppercase text-slate-600">Settings</p>
          {sections.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${section === id ? "text-nx-goldlight bg-nx-gold/[0.08] border-r-2 border-nx-gold" : "text-slate-400 hover:text-white hover:bg-white/[0.03]"}`}
              data-testid={`settings-nav-${id}`}
            >
              <Icon size={15} /> <span className="truncate">{label}</span>
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
            <h3 className="font-marcellus text-lg text-white">{sections.find((s) => s.id === section)?.label}</h3>
            <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors p-1" aria-label="Close settings" data-testid="settings-close">
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            {section === "accounts" && (
              <AccountsSection accounts={accounts} activeEmail={activeEmail} canAddAccount={canAddAccount} onAdd={onAddAccount} onRemove={onRemoveAccount} />
            )}
            {section === "signature" && <SignatureSection onOpenSignature={onOpenSignature} />}
            {section === "password" && <PasswordSection api={api} />}
            {section === "storage" && <StorageSection api={api} activeEmail={activeEmail} />}
            {section === "autoreply" && <AutoReplySection api={api} />}
            {section === "company" && <CompanySection api={api} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountsSection = ({ accounts, activeEmail, canAddAccount = true, onAdd, onRemove }) => (
  <div data-testid="settings-accounts">
    <p className="text-xs text-slate-500 leading-relaxed mb-4">
      Every mailbox signed in under this NexSecure Mail session — like Outlook&rsquo;s <em>Your accounts</em>. Click one in the sidebar to switch to it.
    </p>
    <div className="space-y-2">
      {accounts.map((a) => (
        <div key={a.email} className="flex items-center gap-3 border border-white/10 bg-white/[0.02] px-4 py-3" data-testid={`settings-account-${a.email.replace(/[^a-z0-9]/g, "-")}`}>
          <SenderAvatar email={a.email} size={32} />
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm text-white">{a.email}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500">
              {a.primary ? "Primary sign-in" : "Added mailbox"}{a.email === activeEmail ? " · active now" : ""}
            </p>
          </div>
          {!a.primary && (
            <button
              onClick={() => onRemove(a.email)}
              className="text-[11px] tracking-wide uppercase text-red-400/80 hover:text-red-300 px-3 py-2 transition-colors"
              data-testid={`settings-remove-${a.email.replace(/[^a-z0-9]/g, "-")}`}
            >
              Remove
            </button>
          )}
        </div>
      ))}
    </div>
    {canAddAccount && (
      <button
        onClick={onAdd}
        className="mt-4 flex items-center gap-2 border border-nx-gold/40 text-nx-goldlight hover:bg-nx-gold/10 text-xs font-semibold tracking-wide px-4 py-2.5 transition-colors"
        data-testid="settings-add-account"
      >
        <MailPlus size={14} /> Add account
      </button>
    )}
  </div>
);

const SignatureSection = ({ onOpenSignature }) => (
  <div data-testid="settings-signature">
    <p className="text-xs text-slate-500 leading-relaxed mb-4">
      Your branded signature is appended to every outgoing mail (when &ldquo;Attach my branded signature&rdquo; is ticked in Compose). It is set per mailbox.
    </p>
    <button
      onClick={onOpenSignature}
      className="flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-wide px-5 py-3 transition-colors"
      data-testid="settings-open-signature"
    >
      <PenLine size={14} /> Edit my signature
    </button>
  </div>
);

const PasswordSection = ({ api }) => {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) {
      toast.error("At least 10 characters — use a mix of letters, numbers and symbols");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mail/password/change", { current_password: cur, new_password: pw });
      toast.success("Mailbox password changed on the mail server — update your phone's mail app too");
      setDone(true);
      setCur("");
      setPw("");
      setPw2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <form onSubmit={submit} className="max-w-sm flex flex-col gap-3" data-testid="settings-password">
      <p className="text-xs text-slate-500 leading-relaxed">
        This changes your real mailbox password on the mail server — it applies everywhere: this webmail, Roundcube, and any phone or desktop mail apps (update them with the new password). Use at least 10 characters with letters, numbers and symbols.
      </p>
      <input type="password" required autoComplete="current-password" placeholder="Current password" className={input} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="settings-current-password" />
      <input type="password" required minLength={10} autoComplete="new-password" placeholder="New password (min 10 characters)" className={input} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="settings-new-password" />
      <input type="password" required minLength={10} autoComplete="new-password" placeholder="Confirm new password" className={input} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="settings-confirm-password" />
      <button type="submit" disabled={busy} className="flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm tracking-wide px-6 py-3 transition-colors disabled:opacity-60" data-testid="settings-password-save">
        {busy ? <Loader2 size={15} className="animate-spin" /> : done ? <Check size={15} /> : <KeyRound size={15} />} {busy ? "Saving…" : "Change password"}
      </button>
    </form>
  );
};

const StorageSection = ({ api, activeEmail }) => {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/mail/quota")
      .then(({ data }) => setQuota(data.quota))
      .catch(() => setQuota(null))
      .finally(() => setLoading(false));
  }, [api]);

  const pct = quota ? Math.min(100, (quota.used_kb / quota.limit_kb) * 100) : 0;

  return (
    <div data-testid="settings-storage">
      <p className="text-xs text-slate-500 leading-relaxed mb-4">Mailbox storage for <span className="text-slate-300">{activeEmail}</span>.</p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="storage-loading"><Loader2 size={15} className="animate-spin" /> Checking…</div>
      ) : quota ? (
        <div className="max-w-md" data-testid="storage-quota">
          <div className="h-2.5 bg-white/[0.06] overflow-hidden">
            <div className={`h-full ${pct > 85 ? "bg-red-400" : "bg-nx-gold"}`} style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-2 text-sm text-slate-300">{fmtMB(quota.used_kb)} used of {fmtMB(quota.limit_kb)} <span className="text-slate-500">({pct.toFixed(1)}%)</span></p>
          {pct > 85 && <p className="mt-2 text-xs text-red-400">Almost full — clear Trash and Spam regularly.</p>}
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="storage-unavailable">Storage information is not available for this mailbox.</p>
      )}
    </div>
  );
};

const OOO_INPUT = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-3 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

const CompanySection = ({ api }) => {
  const [hDate, setHDate] = useState("");
  const [hReason, setHReason] = useState("");
  const [gbp, setGbp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const announce = async () => {
    if (!hDate || hReason.trim().length < 3) { toast.error("Pick a date and enter the reason"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/holiday/announce", { date: hDate, reason: hReason.trim() });
      setGbp(data.gbp_text);
      toast.success(`Announced — website notice live, ${data.emailed} staff emailed`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not announce"); }
    finally { setBusy(false); }
  };

  const changeGate = async () => {
    if (nw.length < 8) { toast.error("New password: at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/portal/staff-gate/change", { current: cur, new: nw });
      toast.success("Staff page password changed");
      setCur(""); setNw("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not change"); }
    finally { setPwBusy(false); }
  };

  return (
    <div className="flex flex-col gap-8" data-testid="settings-company">
      <div>
        <p className="text-sm text-slate-200 mb-1">Holiday announcement</p>
        <p className="text-xs text-slate-500 mb-3">Posts a notice on the website homepage and emails all staff from ceo@nexsecure.in. A ready-to-paste Google Business post is generated below.</p>
        <div className="flex flex-col gap-3 max-w-sm">
          <input type="date" className={OOO_INPUT} value={hDate} onChange={(e) => setHDate(e.target.value)} data-testid="holiday-date" />
          <input type="text" maxLength={300} placeholder="Reason (e.g. Onam, Independence Day)" className={OOO_INPUT} value={hReason} onChange={(e) => setHReason(e.target.value)} data-testid="holiday-reason" />
          <button onClick={announce} disabled={busy} className="self-start text-[10px] tracking-[0.15em] uppercase bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold px-5 py-2.5 transition-colors disabled:opacity-60" data-testid="holiday-announce">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Announce"}
          </button>
          {gbp && (
            <div className="border border-nx-gold/25 bg-nx-gold/[0.04] p-3" data-testid="holiday-gbp-text">
              <p className="text-[10px] tracking-[0.18em] uppercase text-nx-goldlight mb-2">Google Business post — copy &amp; paste</p>
              <pre className="text-xs text-slate-300 whitespace-pre-wrap">{gbp}</pre>
              <button onClick={() => { navigator.clipboard.writeText(gbp); toast.success("Copied"); }} className="mt-2 text-[10px] tracking-[0.12em] uppercase text-nx-goldlight hover:text-nx-gold" data-testid="holiday-gbp-copy">Copy text</button>
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-white/10 pt-6">
        <p className="text-sm text-slate-200 mb-1">Staff page access password</p>
        <p className="text-xs text-slate-500 mb-3">The shared password staff use at nexsecure.in/staff.</p>
        <div className="flex flex-col gap-3 max-w-sm">
          <input type="password" placeholder="Current access password" className={OOO_INPUT} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="gate-current" />
          <input type="password" placeholder="New access password (min 8 chars)" className={OOO_INPUT} value={nw} onChange={(e) => setNw(e.target.value)} data-testid="gate-new" />
          <button onClick={changeGate} disabled={pwBusy} className="self-start text-[10px] tracking-[0.15em] uppercase border border-white/20 text-slate-300 hover:text-white px-5 py-2.5 transition-colors disabled:opacity-60" data-testid="gate-change">
            {pwBusy ? <Loader2 size={14} className="animate-spin" /> : "Change password"}
          </button>
        </div>
      </div>
    </div>
  );
};

const STATUS_BADGE = {
  active: "text-emerald-400 border-emerald-400/30 bg-emerald-400/[0.06]",
  scheduled: "text-nx-goldlight border-nx-gold/30 bg-nx-gold/[0.06]",
  expired: "text-slate-500 border-white/15 bg-white/[0.03]",
  off: "text-slate-500 border-white/15 bg-white/[0.03]",
};

const OOOForm = ({ value, onChange, busy }) => (
  <div className="flex flex-col gap-3">
    <input type="text" placeholder="Reply subject (e.g. Out of office until 5 Sept)" className={OOO_INPUT} maxLength={200}
      value={value.subject} onChange={(e) => onChange({ ...value, subject: e.target.value })} disabled={busy} data-testid="ooo-subject" />
    <textarea rows={4} placeholder={"Reply message…\ne.g. I am away with limited email access. For urgent matters call +91 484 457 0089."}
      className={`${OOO_INPUT} resize-none`} maxLength={5000}
      value={value.body} onChange={(e) => onChange({ ...value, body: e.target.value })} disabled={busy} data-testid="ooo-body" />
    <div className="grid grid-cols-2 gap-3">
      <label className="text-[10px] tracking-[0.15em] uppercase text-slate-500 flex flex-col gap-1">First day
        <input type="date" className={OOO_INPUT} value={value.start_date} onChange={(e) => onChange({ ...value, start_date: e.target.value })} disabled={busy} data-testid="ooo-start-date" />
      </label>
      <label className="text-[10px] tracking-[0.15em] uppercase text-slate-500 flex flex-col gap-1">Last day
        <input type="date" className={OOO_INPUT} value={value.end_date} onChange={(e) => onChange({ ...value, end_date: e.target.value })} disabled={busy} data-testid="ooo-end-date" />
      </label>
    </div>
  </div>
);

const AutoReplySection = ({ api }) => {
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ enabled: false, subject: "", body: "", start_date: "", end_date: "" });
  const [status, setStatus] = useState("off");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/mail/ooo").then(({ data }) => {
      const s = data.settings;
      setForm({ enabled: s.enabled, subject: s.subject, body: s.body, start_date: s.start_date, end_date: s.end_date });
      setStatus(s.status);
      setIsAdmin(data.is_admin);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [api]);

  const loadStaff = () => api.get("/mail/ooo/admin").then(({ data }) => setStaff(data.staff)).catch(() => {});
  useEffect(() => { if (isAdmin) loadStaff(); }, [isAdmin]); // eslint-disable-line

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mail/ooo", next);
      setForm({ enabled: data.settings.enabled, subject: data.settings.subject, body: data.settings.body, start_date: data.settings.start_date, end_date: data.settings.end_date });
      setStatus(data.settings.status);
      toast.success(next.enabled ? "Automatic replies are on" : "Automatic replies are off");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const saveStaff = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mail/ooo/admin", row);
      if (data.warning) toast.warning(data.warning); else toast.success(`Saved for ${row.email}`);
      setEditing(null);
      loadStaff();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!loaded) return <Loader2 size={18} className="animate-spin text-slate-500" />;

  return (
    <div data-testid="settings-autoreply" className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-200">Send automatic replies</p>
          <p className="text-xs text-slate-500 mt-0.5">Anyone who emails you during the period gets your reply — once per sender per day.</p>
        </div>
        <button type="button" disabled={busy}
          onClick={() => (form.enabled ? save({ ...form, enabled: false }) : setForm({ ...form, enabled: true }))}
          className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${form.enabled ? "bg-nx-gold" : "bg-white/15"}`}
          aria-label="Toggle automatic replies" data-testid="ooo-toggle">
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${form.enabled ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>
      <span className={`self-start text-[10px] tracking-[0.18em] uppercase border px-2.5 py-1 ${STATUS_BADGE[status]}`} data-testid="ooo-status">
        {status === "active" ? "On — replying now" : status === "scheduled" ? "Scheduled" : status === "expired" ? "Period ended" : "Off"}
      </span>
      {form.enabled && (
        <>
          <OOOForm value={form} onChange={setForm} busy={busy} />
          <button onClick={() => save({ ...form, enabled: true })} disabled={busy}
            className="self-start inline-flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold px-5 py-2.5 transition-colors disabled:opacity-60"
            data-testid="ooo-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save automatic replies
          </button>
        </>
      )}

      {isAdmin && staff && (
        <div className="border-t border-white/10 pt-5" data-testid="ooo-admin-list">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">Staff out-of-office (admin)</p>
          <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
            {staff.map((s) => (
              <div key={s.email} className="border border-white/10 bg-white/[0.02] px-3.5 py-3" data-testid={`ooo-admin-row-${s.email}`}>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-200 flex-1 truncate">{s.email}</span>
                  <span className={`text-[9px] tracking-[0.15em] uppercase border px-2 py-0.5 ${STATUS_BADGE[s.status]}`}>{s.status}</span>
                  {s.enabled && !s.worker_ready && <span className="text-[9px] text-amber-400" title="User must sign in to webmail once">needs sign-in</span>}
                  <button onClick={() => setEditing(editing === s.email ? null : { ...s })}
                    className="text-[10px] tracking-[0.12em] uppercase text-nx-goldlight hover:text-nx-gold transition-colors"
                    data-testid={`ooo-admin-edit-${s.email}`}>
                    {editing && editing.email === s.email ? "Close" : "Manage"}
                  </button>
                </div>
                {editing && editing.email === s.email && (
                  <div className="mt-3 flex flex-col gap-3">
                    <OOOForm value={editing} onChange={setEditing} busy={busy} />
                    <div className="flex gap-2">
                      <button onClick={() => saveStaff({ ...editing, enabled: true })} disabled={busy}
                        className="text-[10px] tracking-[0.12em] uppercase bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold px-4 py-2 transition-colors disabled:opacity-60"
                        data-testid="ooo-admin-save">Turn on & save</button>
                      {s.enabled && (
                        <button onClick={() => saveStaff({ ...editing, enabled: false })} disabled={busy}
                          className="text-[10px] tracking-[0.12em] uppercase border border-white/20 text-slate-300 hover:text-white px-4 py-2 transition-colors disabled:opacity-60"
                          data-testid="ooo-admin-disable">Turn off</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
